import { DiscordSDK } from "@discord/embedded-app-sdk";
import { apiFetch } from "../auth/api";
import type { SessionUser } from "../auth/session";
import { isDiscordFrame } from "../utils/frame";

export interface DiscordIdentity {
  accessToken: string;
  user: SessionUser;
}

let cached: Promise<DiscordIdentity> | null = null;

// Singleton SDK shared by auth + Rich Presence (setActivity). Creating one
// DiscordSDK per flow breaks the embedded frame handshake, so every caller
// must go through ensureSdk()/ensureDiscordSdkReady().
let sdkInstance: DiscordSDK | null = null;

// Tracks whether sdk.ready() completed so stored-auth paths (cookie/JWT +
// /me, token refresh, reconnect) can reuse the handshake without redoing it.
let sdkReadyDone = false;
let sdkReadyInflight: Promise<DiscordSDK | null> | null = null;

export function getDiscordSdk(): DiscordSDK | null {
  return sdkInstance;
}

/** Best-effort clear of the Rich Presence activity. Never throws. */
export async function clearDiscordPresence(): Promise<void> {
  try {
    const sdk = sdkInstance;
    if (!sdk) return;
    await sdk.commands.setActivity({
      // Null activity clears Rich Presence (typed as unknown: the zod
      // schema is nullable at runtime).
      activity: null as unknown as Parameters<typeof sdk.commands.setActivity>[0]["activity"],
    });
  } catch {
    return;
  }
}

function clientId(): string {
  try {
    return (import.meta.env.VITE_DISCORD_CLIENT_ID as string | undefined) ?? "";
  } catch {
    return "";
  }
}

export function getDiscordAuth(): Promise<DiscordIdentity> {
  if (!cached) {
    const task = runDiscordAuth();
    cached = task;
    // Self-heal: a rejected attempt must not poison the cache forever.
    task.catch(() => {
      if (cached === task) cached = null;
    });
  }
  return cached;
}

export function resetDiscordAuth(): void {
  cached = null;
}

function ensureSdk(id: string): DiscordSDK {
  if (!sdkInstance) sdkInstance = new DiscordSDK(id);
  return sdkInstance;
}

/**
 * Ensure the shared SDK handshake completed without running the full auth
 * flow. Used by Rich Presence on stored-auth paths (cookie/JWT + /me
 * success, token refresh, WS reconnect) where runDiscordAuth was skipped and
 * sdkInstance would otherwise stay null. Deduped, never throws.
 */
export function ensureDiscordSdkReady(): Promise<DiscordSDK | null> {
  const id = clientId();
  if (!id) return Promise.resolve(null);
  const sdk = ensureSdk(id);
  if (sdkReadyDone) return Promise.resolve(sdk);
  if (sdkReadyInflight) return sdkReadyInflight;
  sdkReadyInflight = sdk
    .ready()
    .then(() => {
      sdkReadyDone = true;
      sdkReadyInflight = null;
      return sdk;
    })
    .catch(() => {
      sdkReadyInflight = null;
      return null;
    });
  return sdkReadyInflight;
}

/**
 * Background full Discord auth (authorize scopes incl. rpc.activities.write +
 * authenticate) so presence works even when the session came from a stored
 * JWT instead of a fresh reauth. Fire-and-forget: never throws, never
 * overrides the stored session.
 */
export function warmDiscordAuth(): void {
  try {
    if (!isDiscordFrame()) return;
    void getDiscordAuth().catch(() => undefined);
  } catch {
    return;
  }
}

type RawUser = Partial<SessionUser> & { user_id?: string; name?: string };

async function fetchSdkUser(
  sdk: DiscordSDK,
  discordAccessToken: string,
): Promise<SessionUser> {
  const auth = await sdk.commands.authenticate({ access_token: discordAccessToken });
  let username = auth.user.username;
  let avatar: string | null = auth.user.avatar ?? null;
  try {
    const u = await sdk.commands.getUser({ id: auth.user.id });
    if (u) {
      username = u.username ?? username;
      avatar = u.avatar ?? avatar;
    }
  } catch {
    return { id: auth.user.id, username, avatar };
  }
  return { id: auth.user.id, username, avatar };
}

async function runDiscordAuth(): Promise<DiscordIdentity> {
  const id = clientId();
  if (!id) throw new Error("missing VITE_DISCORD_CLIENT_ID");
  const sdk = ensureSdk(id);
  await sdk.ready();
  sdkReadyDone = true;
  const frame = isDiscordFrame();
  // Frame (embedded) sessions request rpc.activities.write for Rich Presence
  // via setActivity. If the user denies the extra scope, fall back to
  // identify-only and continue without presence — auth/game must never break
  // on consent fail. No persistent denied flag: every fresh authorize retry
  // re-attempts the presence scope.
  let code: string;
  if (frame) {
    try {
      const res = await sdk.commands.authorize({
        client_id: id,
        response_type: "code",
        scope: ["identify", "rpc.activities.write"],
        prompt: "none",
      });
      code = res.code;
    } catch {
      const retry = await sdk.commands.authorize({
        client_id: id,
        response_type: "code",
        scope: ["identify"],
        prompt: "none",
      });
      code = retry.code;
    }
  } else {
    const res = await sdk.commands.authorize({
      client_id: id,
      response_type: "code",
      scope: ["identify", "guilds"],
      prompt: "none",
    });
    code = res.code;
  }
  const data = await apiFetch<{ token?: string; access_token?: string; user?: RawUser }>(
    "/api/auth/discord-token",
    { method: "POST", body: { code } },
  );
  const sessionToken = data.token ?? "";
  if (!sessionToken) throw new Error("discord-token: no token");
  const discordAccessToken = data.access_token ?? "";
  if (data.user) {
    const u = data.user;
    const uid = u.id ?? u.user_id ?? "";
    const username = u.username ?? u.name ?? "";
    if (uid && username) {
      const user: SessionUser = { id: uid, username, avatar: u.avatar ?? null };
      if (discordAccessToken !== "" && discordAccessToken !== sessionToken) {
        try {
          await sdk.commands.authenticate({ access_token: discordAccessToken });
        } catch {
          return { accessToken: sessionToken, user };
        }
      }
      return { accessToken: sessionToken, user };
    }
  }
  if (!discordAccessToken) throw new Error("discord-token: no access_token");
  const user = await fetchSdkUser(sdk, discordAccessToken);
  return { accessToken: sessionToken, user };
}
